import { appConfig } from '_/config/app';
import { colors } from '_/space-management/constants/theme';
import { reportsApi } from '_/space-management/services/api';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showMessage } from 'react-native-flash-message';
import {
  ReportContextData,
  ReportsModel,
  LoadingType,
} from '_/space-management/services/models/reports.model';

const ReportsContext = createContext<ReportContextData>({} as ReportContextData);

type ReportsType = {
  children?: React.ReactNode;
};

export const ReportsProvider: React.FC<ReportsType> = ({ children }) => {
  const [openListEndReached, setOpenListEndReached] = useState(false);
  const [openListPage, setOpenListPage] = useState(0);
  const [openReports, setOpenReports] = useState<ReportsModel[] | undefined>();
  const [closedListEndReached, setClosedListEndReached] = useState(false);
  const [closedListPage, setClosedListPage] = useState(0);
  const [closedReports, setClosedReports] = useState<ReportsModel[] | undefined>();
  const [loading, setLoading] = useState<LoadingType>();
  const { t } = useTranslation();

  const getOpenReports = useCallback(
    async (reset?: boolean) => {
      try {
        setLoading(reset ? 'open' : 'open-end');
        const page = reset ? 0 : openListPage + 1;
        setOpenListPage(page);
        const { data } = await reportsApi.getList({
          completedAt: '<null>',
          '$sort[createdAt]': -1,
          $limit: appConfig.spaceManagement.reportLimit,
          $skip: page * appConfig.spaceManagement.reportLimit,
        });

        setOpenListEndReached(data.length === 0);
        if (reset) {
          setOpenReports(data);
        } else {
          setOpenReports((prevReports) =>
            prevReports?.concat(data.filter((report: any) => prevReports.indexOf(report) === -1))
          );
        }
      } catch (error) {
        showMessage({
          message: t('spaceManage.error'),
          description: t('spaceManage.reportsScreen.errorMessages.getReports'),
          backgroundColor: colors.errorRed,
        });
      }
      setLoading(undefined);
    },
    [openListPage, t]
  );

  const getClosedReports = useCallback(
    async (reset?: boolean) => {
      try {
        setLoading(reset ? 'closed' : 'closed-end');
        const page = reset ? 0 : closedListPage + 1;
        setClosedListPage(page);
        const { data } = await reportsApi.getList({
          '$sort[createdAt]': -1,
          'completedAt[$ne]': '<null>',
          $limit: appConfig.spaceManagement.reportLimit,
          $skip: page * appConfig.spaceManagement.reportLimit,
        });

        setClosedListEndReached(data.length === 0);
        if (reset) {
          setClosedReports(data);
        } else {
          setClosedReports((prevReports) =>
            prevReports?.concat(data.filter((report: any) => prevReports.indexOf(report) === -1))
          );
        }
      } catch (error) {
        showMessage({
          message: t('spaceManage.error'),
          description: t('spaceManage.reportsScreen.errorMessages.getReports'),
          backgroundColor: colors.errorRed,
        });
      }
      setLoading(undefined);
    },
    [closedListPage, t]
  );

  return (
    <ReportsContext.Provider
      value={{
        getOpenReports,
        getClosedReports,
        openReports,
        closedReports,
        openListEndReached,
        closedListEndReached,
        loading,
      }}
    >
      {children}
    </ReportsContext.Provider>
  );
};

export function useReportsContext(): ReportContextData {
  const context = useContext(ReportsContext);

  if (!context) {
    throw new Error('useReportsContext must be used within an ReportsProvider');
  }

  return context;
}
